import React, { useMemo, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';

import { Box, Body4, IconButton, SvgIcon } from 'components/core';
import { Player } from 'components/player';
import { ProgressLoader } from 'components/loading';

import API from 'services/api';
import { colors } from 'styles/theme';
import { ALL_IMAGES } from 'styles/images';
import { getAssetType, isImgix } from 'utils/form';

const Image = styled.img`
  height: 204px;

  @media (max-width: 768px) {
    height: 102px;
  }
`;

const PreviewContainer = styled(Box)`
  position: relative;
  min-height: 223px;
  padding: 0;
  width: 368px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 304px;
  }
`;

const PreviewOverlay = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ImagePreview = styled.img`
  height: 223px;
  width: 280px;
  object-fit: cover;
`;

const VideoPreview = styled(Box)`
  height: 223px;
  width: 280px;
  max-height: 223px;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 4px;
  right: 4px;
`;

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  borderWidth: 1,
  borderRadius: 6,
  borderColor: colors.text,
  borderStyle: 'none',
  backgroundColor: 'transparent',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  width: '280px',
  minHeight: '223px',
};

const activeStyle = {
  borderColor: colors.text,
  backgroundColor: colors.line,
};

const acceptStyle = {
  borderColor: colors.text,
};

const rejectStyle = {
  borderColor: colors.red,
};

function FormUpload({
  category,
  uploadKey,
  onUpload,
  onUploadProgress,
  thumbUrl,
  assetType,
  assetUrl,
  onReset,
  ...props
}) {
  const [preview, setPreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState();

  const resetUpload = () => {
    setPreview('');
    if (onReset) {
      onReset();
    }
    if (onUploadProgress) {
      onUploadProgress(false);
    }
  };

  const uploadCallback = (progressEvent) => {
    var percentCompleted = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setUploadProgress(percentCompleted);
  };

  const uploadFile = useCallback(
    async (fileToUpload) => {
      setIsUploading(true);
      setUploadProgress(0);
      if (onUploadProgress) {
        onUploadProgress(true);
      }

      const type = getAssetType(fileToUpload.type);
      const fileKey = `${uploadKey}-${fileToUpload.name}`;

      if (type === 'image') {
        setPreview(URL.createObjectURL(fileToUpload));
      } else {
        setPreview(type);
      }

      const uploadAssetUrl = await API.uploadFile(
        fileToUpload,
        fileKey,
        uploadCallback
      );
      if (onUpload) {
        onUpload({ assetUrl: uploadAssetUrl, type, fileKey });
      }

      setIsUploading(false);
      setPreview('');
    },
    [setIsUploading, uploadKey, onUpload, onUploadProgress]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles || !acceptedFiles.length) {
        return;
      }
      const file = acceptedFiles[0];
      uploadFile(file);
    },
    [uploadFile]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ accept: 'image/*, video/*, application/pdf', onDrop });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignSelf="stretch"
      alignItems="center"
      justifyContent="center"
      {...props}
    >
      {!preview && !thumbUrl && (
        <div {...getRootProps({ style })}>
          <Image src={ALL_IMAGES[category]} alt={category} />
          <input {...getInputProps()} />
          <Body4 p={3}>Upload photo, video or PDF</Body4>
        </div>
      )}
      {preview && (
        <PreviewContainer>
          <CloseButton borderWidth={0} onClick={resetUpload}>
            <SvgIcon name="delete" />
          </CloseButton>
          {isUploading && (
            <PreviewOverlay>
              <ProgressLoader
                value={uploadProgress}
                text={`${uploadProgress}%`}
              />
            </PreviewOverlay>
          )}
        </PreviewContainer>
      )}
      {thumbUrl && (
        <PreviewContainer>
          {assetType === 'video' && !isUploading ? (
            <VideoPreview>
              <Player
                isFluid={false}
                data={{
                  assetUrl,
                  assetType,
                  thumbUrl: isImgix(thumbUrl)
                    ? `${thumbUrl}?h=223&w=280`
                    : thumbUrl,
                }}
                category="preview"
              />
            </VideoPreview>
          ) : (
            <ImagePreview
              src={isImgix(thumbUrl) ? `${thumbUrl}?h=223&w=342` : thumbUrl}
            />
          )}
          <CloseButton borderWidth={0} onClick={resetUpload}>
            <SvgIcon name="delete" />
          </CloseButton>
        </PreviewContainer>
      )}
    </Box>
  );
}

export default FormUpload;
